<script setup lang="ts">
/**
 * Formatting the text being written, from the inspector: everything a word
 * or a paragraph can be set in, with the room to show it.
 *
 * Acts on the selection of the editor in use on the page (see
 * `useTextFormatting`), and shows what is in force there as the caret moves:
 * the named style, bold to subscript, the size, the colour and the
 * highlight, the paragraph's alignment, lists and quotation, the link.
 * Every colour is one of the palette's (see `domain/prose.ts`), never any
 * colour, so the text stays in the house's colours and survives the
 * sanitiser.
 *
 * With nothing being written, it says where to click instead.
 */
import { useI18n } from 'vue-i18n'

import type { Editor } from '@tiptap/vue-3'
import { computed, ref, watch } from 'vue'

import { useTextFormatting } from '@/composables/useTextFormatting'
import {
  BASE_INKS,
  HOUSE_INKS,
  INK_SWATCHES,
  MARK_SWATCHES,
  PROSE_ALIGNS,
  PROSE_INKS,
  PROSE_MARKS,
  PROSE_SIZES,
  type BaseInk,
  type ProseAlign,
  type ProseInk,
  type ProseMark,
  type ProseSize,
} from '@/domain/prose'
import { readAccent } from '@/domain/layout/colour'
import InspectorField from '@/sections/InspectorField.vue'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const report = useReportStore()
const { editor, tick } = useTextFormatting()

type Chain = ReturnType<Editor['chain']>

/** The editor in use, or none. */
const live = computed(() => editor.value)

/**
 * The editor, read so that whatever reads it follows every change in it: the
 * same editor object comes back each time, so it is the counter of its
 * changes that has to be depended on, not the editor.
 */
function current(): Editor | null {
  void tick.value
  return editor.value
}

function on(name: string, attributes?: Record<string, unknown>): boolean {
  return current()?.isActive(name, attributes) ?? false
}

function run(act: (chain: Chain) => Chain): void {
  const chain = editor.value?.chain().focus()
  if (chain) act(chain).run()
}

function can(act: (chain: Chain) => Chain): boolean {
  const instance = current()
  return instance ? act(instance.can().chain().focus()).run() : false
}

// ── Characters ───────────────────────────────────────────────────────────

const CHARACTERS = [
  { id: 'bold', glyph: 'B', toggle: (chain: Chain) => chain.toggleBold() },
  { id: 'italic', glyph: 'I', toggle: (chain: Chain) => chain.toggleItalic() },
  { id: 'underline', glyph: 'U', toggle: (chain: Chain) => chain.toggleUnderline() },
  { id: 'strike', glyph: 'S', toggle: (chain: Chain) => chain.toggleStrike() },
  { id: 'superscript', glyph: 'x²', toggle: (chain: Chain) => chain.toggleSuperscript() },
  { id: 'subscript', glyph: 'x₂', toggle: (chain: Chain) => chain.toggleSubscript() },
] as const

const size = computed<ProseSize | 'normal'>(
  () => PROSE_SIZES.find((name) => on('proseSize', { value: name })) ?? 'normal',
)

function setSize(name: ProseSize | 'normal'): void {
  run((chain) => (name === 'normal' ? chain.unsetSize() : chain.setSize(name)))
}

// ── Colours ──────────────────────────────────────────────────────────────

function inkSwatch(name: BaseInk): string {
  if (name === 'accent') return readAccent(report.layout.accent)
  if (name === 'ink') return '#16202c'
  return INK_SWATCHES[name]
}

/** The charter's colours, each under the ink that names its place. */
const houseInks = computed(() =>
  report.layout.palette.flatMap((colour, at) => {
    const name = HOUSE_INKS[at]
    return name
      ? [{ name, colour, label: t('inspector.text.format.inks.house', { n: at + 1 }) }]
      : []
  }),
)

const ink = computed(() => PROSE_INKS.find((name) => on('proseInk', { value: name })) ?? null)
const highlight = computed(
  () => PROSE_MARKS.find((name) => on('proseHighlight', { value: name })) ?? null,
)

function setInk(name: ProseInk | null): void {
  run((chain) => (name ? chain.setInk(name) : chain.unsetInk()))
}

function setHighlight(name: ProseMark | null): void {
  run((chain) => (name ? chain.setHighlight(name) : chain.unsetHighlight()))
}

// ── Named styles ─────────────────────────────────────────────────────────

const textStyles = computed(() => report.layout.textStyles)

const textStyle = computed(
  () => (current()?.getAttributes('reportTextStyle')['styleId'] as string | undefined) ?? '',
)

function chooseTextStyle(styleId: string): void {
  run((chain) => (styleId ? chain.setReportTextStyle(styleId) : chain.unsetReportTextStyle()))
}

// ── Paragraphs ───────────────────────────────────────────────────────────

const ALIGN_ICONS: Record<ProseAlign, string> = {
  left: 'M2.5 4h11M2.5 7h7M2.5 10h11M2.5 13h7',
  center: 'M2.5 4h11M4.5 7h7M2.5 10h11M4.5 13h7',
  right: 'M2.5 4h11M6.5 7h7M2.5 10h11M6.5 13h7',
  justify: 'M2.5 4h11M2.5 7h11M2.5 10h11M2.5 13h11',
}

const align = computed(() => PROSE_ALIGNS.find((name) => on('paragraph', { align: name })) ?? null)

/** The alignment already in force taken off again, back to the block's. */
function setAlign(name: ProseAlign): void {
  run((chain) => (align.value === name ? chain.unsetAlign() : chain.setAlign(name)))
}

const PARAGRAPH_TOOLS = [
  {
    id: 'bullets',
    icon: ['M6 4.5h7.5M6 8h7.5M6 11.5h7.5', 'M3 4.5h.01M3 8h.01M3 11.5h.01'],
    active: () => on('bulletList'),
    act: (chain: Chain) => chain.toggleBulletList(),
  },
  {
    id: 'numbers',
    icon: ['M6.5 4.5h7M6.5 8h7M6.5 11.5h7', 'M2.5 3.5l1-.5v3M2.5 9.5h1.5l-1.5 2h1.5'],
    active: () => on('orderedList'),
    act: (chain: Chain) => chain.toggleOrderedList(),
  },
  {
    id: 'outdent',
    icon: ['M6.5 4.5h7M6.5 8h7M6.5 11.5h7', 'M4.5 6l-2 2 2 2'],
    active: () => false,
    act: (chain: Chain) => chain.liftListItem('listItem'),
  },
  {
    id: 'indent',
    icon: ['M6.5 4.5h7M6.5 8h7M6.5 11.5h7', 'M2.5 6l2 2-2 2'],
    active: () => false,
    act: (chain: Chain) => chain.sinkListItem('listItem'),
  },
  {
    id: 'quote',
    icon: [
      'M3.5 10.5V8.5a2 2 0 0 1 2-2',
      'M3.5 10.5h2V8.5h-2',
      'M9 10.5V8.5a2 2 0 0 1 2-2',
      'M9 10.5h2V8.5H9',
    ],
    active: () => on('blockquote'),
    act: (chain: Chain) => chain.toggleBlockquote(),
  },
] as const

// ── Links ────────────────────────────────────────────────────────────────

const address = ref('')

// The address of the link the caret is in, offered to change or remove.
watch(
  () => (current()?.getAttributes('link')['href'] as string | undefined) ?? '',
  (href) => (address.value = href),
  { immediate: true },
)

function applyLink(): void {
  const href = address.value.trim()
  if (!href) {
    removeLink()
    return
  }
  run((chain) => chain.extendMarkRange('link').setLink({ href }))
}

function removeLink(): void {
  run((chain) => chain.extendMarkRange('link').unsetLink())
  address.value = ''
}

function plain(): void {
  run((chain) => chain.unsetAllMarks().unsetAlign())
}
</script>

<template>
  <div class="format">
    <p v-if="!live" class="format__idle">{{ t('inspector.text.format.idle') }}</p>

    <fieldset class="format__tools" :disabled="!live">
      <InspectorField v-if="textStyles.length" :label="t('inspector.text.format.style')">
        <select
          class="b-select"
          :value="textStyle"
          @change="chooseTextStyle(($event.target as HTMLSelectElement).value)"
        >
          <option value="">{{ t('editor.textStyleNone') }}</option>
          <option v-for="style in textStyles" :key="style.id" :value="style.id">
            {{ style.name }}
          </option>
        </select>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.characters')">
        <div class="tools" role="group" :aria-label="t('inspector.text.format.characters')">
          <button
            v-for="tool in CHARACTERS"
            :key="tool.id"
            type="button"
            class="tool"
            :class="[`tool--${tool.id}`, { 'tool--on': on(tool.id) }]"
            :aria-pressed="on(tool.id)"
            :title="t(`editor.${tool.id}`)"
            :aria-label="t(`editor.${tool.id}`)"
            @mousedown.prevent
            @click="run(tool.toggle)"
          >
            {{ tool.glyph }}
          </button>
        </div>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.size')">
        <div class="options" role="radiogroup" :aria-label="t('inspector.text.format.size')">
          <button
            v-for="name in ['small', 'normal', 'large', 'xlarge'] as const"
            :key="name"
            type="button"
            role="radio"
            class="option"
            :class="{ 'option--on': size === name }"
            :aria-checked="size === name"
            @mousedown.prevent
            @click="setSize(name)"
          >
            {{ t(`inspector.text.format.sizes.${name}`) }}
          </button>
        </div>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.ink')">
        <div class="swatches" role="radiogroup" :aria-label="t('inspector.text.format.ink')">
          <button
            type="button"
            role="radio"
            class="swatch swatch--none"
            :class="{ 'swatch--on': ink === null }"
            :aria-checked="ink === null"
            :title="t('inspector.text.format.inks.auto')"
            :aria-label="t('inspector.text.format.inks.auto')"
            @mousedown.prevent
            @click="setInk(null)"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M3 13L13 3"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <button
            v-for="name in BASE_INKS"
            :key="name"
            type="button"
            role="radio"
            class="swatch"
            :class="{ 'swatch--on': ink === name }"
            :style="{ '--swatch': inkSwatch(name) }"
            :aria-checked="ink === name"
            :title="t(`inspector.text.format.inks.${name}`)"
            :aria-label="t(`inspector.text.format.inks.${name}`)"
            @mousedown.prevent
            @click="setInk(name)"
          />
        </div>
        <div
          v-if="houseInks.length > 0"
          class="swatches swatches--house"
          role="radiogroup"
          :aria-label="t('inspector.text.format.houseInks')"
        >
          <span class="swatches__label">{{ t('inspector.text.format.houseInks') }}</span>
          <button
            v-for="house in houseInks"
            :key="house.name"
            type="button"
            role="radio"
            class="swatch"
            :class="{ 'swatch--on': ink === house.name }"
            :style="{ '--swatch': house.colour }"
            :aria-checked="ink === house.name"
            :title="house.label"
            :aria-label="house.label"
            @mousedown.prevent
            @click="setInk(house.name)"
          />
        </div>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.highlight')">
        <div class="swatches" role="radiogroup" :aria-label="t('inspector.text.format.highlight')">
          <button
            type="button"
            role="radio"
            class="swatch swatch--none"
            :class="{ 'swatch--on': highlight === null }"
            :aria-checked="highlight === null"
            :title="t('inspector.text.format.highlights.none')"
            :aria-label="t('inspector.text.format.highlights.none')"
            @mousedown.prevent
            @click="setHighlight(null)"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M3 13L13 3"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <button
            v-for="name in PROSE_MARKS"
            :key="name"
            type="button"
            role="radio"
            class="swatch"
            :class="{ 'swatch--on': highlight === name }"
            :style="{ '--swatch': MARK_SWATCHES[name] }"
            :aria-checked="highlight === name"
            :title="t(`inspector.text.format.highlights.${name}`)"
            :aria-label="t(`inspector.text.format.highlights.${name}`)"
            @mousedown.prevent
            @click="setHighlight(name)"
          />
        </div>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.paragraph')">
        <div class="rows">
          <div class="tools" role="radiogroup" :aria-label="t('inspector.text.format.align')">
            <button
              v-for="name in PROSE_ALIGNS"
              :key="name"
              type="button"
              role="radio"
              class="tool"
              :class="{ 'tool--on': align === name }"
              :aria-checked="align === name"
              :title="t(`inspector.text.format.aligns.${name}`)"
              :aria-label="t(`inspector.text.format.aligns.${name}`)"
              @mousedown.prevent
              @click="setAlign(name)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true"><path :d="ALIGN_ICONS[name]" /></svg>
            </button>
          </div>
          <div class="tools" role="group" :aria-label="t('inspector.text.format.lists')">
            <button
              v-for="tool in PARAGRAPH_TOOLS"
              :key="tool.id"
              type="button"
              class="tool"
              :class="{ 'tool--on': tool.active() }"
              :aria-pressed="tool.active()"
              :disabled="!tool.active() && !can(tool.act)"
              :title="t(`inspector.text.format.paragraphTools.${tool.id}`)"
              :aria-label="t(`inspector.text.format.paragraphTools.${tool.id}`)"
              @mousedown.prevent
              @click="run(tool.act)"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path v-for="(d, at) in tool.icon" :key="at" :d="d" />
              </svg>
            </button>
          </div>
        </div>
      </InspectorField>

      <InspectorField :label="t('inspector.text.format.link')">
        <div class="link">
          <input
            v-model="address"
            class="b-input"
            type="url"
            :placeholder="t('inspector.text.format.linkPlaceholder')"
            @keydown.enter.prevent="applyLink"
          />
          <button type="button" class="b-btn b-btn--outline" @click="applyLink">
            {{ t('inspector.text.format.linkApply') }}
          </button>
          <button
            v-if="on('link')"
            type="button"
            class="b-btn b-btn--outline"
            :title="t('inspector.text.format.linkRemove')"
            @click="removeLink"
          >
            {{ t('inspector.text.format.linkRemove') }}
          </button>
        </div>
      </InspectorField>

      <button type="button" class="b-btn b-btn--soft plain" @mousedown.prevent @click="plain">
        {{ t('editor.plain') }}
      </button>
    </fieldset>
  </div>
</template>

<style scoped>
.format,
.format__tools {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.format__tools {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.format__tools:disabled {
  opacity: 0.5;
}

.format__idle {
  margin: 0;
  padding: var(--sp-4) var(--sp-5);
  border: 1px dashed var(--hairline-strong);
  border-radius: var(--radius);
  color: var(--ink-soft);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

/* Toggles in one strip: on in the accent. */
.tools {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
  align-self: flex-start;
}

.tool {
  display: grid;
  place-items: center;
  min-width: 32px;
  height: 28px;
  padding: 0 var(--sp-3);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-family: var(--font-document);
  font-size: var(--text-base);
  cursor: pointer;
}

.tool svg {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.35;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tool:hover:not(:disabled) {
  color: var(--ink);
}

.tool--on {
  background: var(--accent-quiet);
  color: var(--accent);
}

.tool:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.tool--bold {
  font-weight: 700;
}

.tool--italic {
  font-style: italic;
}

.tool--underline {
  text-decoration: underline;
}

.tool--strike {
  text-decoration: line-through;
}

.options {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.option {
  height: 28px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
}

.option--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

/* A colour to pick: the colour itself, ringed in the accent when it is the one in force. */
.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
}

/* The charter's colours, on a line of their own under the fixed ones. */
.swatches--house {
  align-items: center;
  margin-top: var(--sp-3);
}

.swatches__label {
  margin-right: var(--sp-2);
  color: var(--ink-faint);
  font-size: var(--text-xs);
}

.swatch {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--hairline-strong);
  border-radius: 50%;
  background: var(--swatch, transparent);
  cursor: pointer;
}

.swatch--none {
  display: grid;
  place-items: center;
  background: var(--panel);
  color: var(--danger);
}

.swatch--none svg {
  width: 14px;
  height: 14px;
}

.swatch--on {
  box-shadow:
    0 0 0 2px var(--panel),
    0 0 0 4px var(--accent-fill);
}

.link {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: var(--sp-2);
}

.plain {
  align-self: flex-start;
}
</style>
